import {computed, Injectable, signal} from '@angular/core';
import {
    ChartMode,
    DynamicYields,
    ICanvasJSAxisOptions,
    ICanvasJSChartOptions,
    ICanvasJSDataSeriesOptions,
    ICanvasJSLegend,
    ICanvasJSToolTip,
    SingleDate,
    StatisticsState,
    TimePeriod,
    YieldsScopes
} from "../../models";
import {ParametersService} from "../parameters.service";
import {BehaviorSubject} from "rxjs";
import {CHART_PALETTES, CHART_MODE_COLORS} from "./chart-color-schemes";

@Injectable({
    providedIn: 'root'
})
export class StatisticsService {
    currentGeneratedType: null | StatisticsState | DynamicYields = null;
    messageContent: string = '';
    singleClick: boolean = false;
    state: StatisticsState | DynamicYields = 'temperatures';
    timePeriod: TimePeriod = 'day';
    selectedMonth: number;
    selectedYear: string;
    statisticsChartReady = new BehaviorSubject<ICanvasJSChartOptions | null>(null);
    statisticsChartReady$ = this.statisticsChartReady.asObservable();

    chartMode = signal<ChartMode>('dark');
    palette = computed(() => CHART_PALETTES[this.chartMode()]);
    modeColors = computed(() => CHART_MODE_COLORS[this.chartMode()]);
    basicColor = computed(() => this.modeColors().basicColor);
    oppositeColor = computed(() => this.modeColors().oppositeColor);
    background = computed(() => this.modeColors().background);
    toolTipBackground = computed(() => this.modeColors().toolTipBackground);

    constructor(
        private parameters: ParametersService,
    ) {
        const t = new Date();
        this.selectedMonth = t.getMonth() + 1;
        this.selectedYear  = t.getFullYear().toString();
    }

    makeDate(el: string): SingleDate {
        const singleDate: SingleDate = {
            year: parseInt(el.substring(0, 4),10),
            month: parseInt(el.substring(4, 6),10),
            day: parseInt(el.substring(6, 8),10),
            hour: 0,
            minute: 0,
        }
        if(this.parameters.modules[this.parameters.selectedModuleIndex].gmtTime !== null) {
            singleDate.hour = parseFloat(el.substring(8, 10))
                + parseFloat(this.parameters.modules[this.parameters.selectedModuleIndex].gmtTime) - 1
        } else {
            singleDate.hour = parseInt(el.substring(8, 10),10);
        }
        singleDate.minute = parseInt(el.substring(10, 12),10);

        return singleDate;
    }

    isDynamicYields(): boolean {
        return this.isDynamicYieldsGuard(this.state);
    }

    setTimePeriodForDynamicYields() {
        if (this.isDynamicYieldsGuard(this.state)) {
            if (this.state.hour) {
                this.timePeriod = 'hour';
                return;
            }
            if (this.state.day) {
                this.timePeriod = 'day';
                return;
            }
            if (this.state.week) {
                this.timePeriod = 'week';
                return;
            }
            if (this.state.month) {
                this.timePeriod = 'month';
                return;
            }
            if (this.state.year) {
                this.timePeriod = 'year';
                return;
            }
            if (this.state.total) {
                this.timePeriod = 'total';
                return;
            }
        }
    }

    isDynamicYieldsGuard(obj: any): obj is DynamicYields {
        return obj
            && typeof obj.objectId === 'number'
            && typeof obj.unit === 'number'
            && typeof obj.txtId === 'number'
            && typeof obj.instantaneous === 'boolean'
            && typeof obj.hour === 'boolean'
            && typeof obj.day === 'boolean'
            && typeof obj.week === 'boolean'
            && typeof obj.month === 'boolean'
            && typeof obj.year === 'boolean'
            && typeof obj.total === 'boolean';
    }

    isYieldStateGuard(obj: any): obj is keyof YieldsScopes {
        return !this.isDynamicYieldsGuard(obj) && obj !== 'temperatures';
    }

    rerenderChart(mode: ChartMode): void {
        this.chartMode.set(mode);
        const chartData = this.statisticsChartReady.getValue();
        if (!chartData) {
            return;
        }
        this.statisticsChartReady.next(this.applyChartMode(chartData));
    }

    applyChartMode(chartData: ICanvasJSChartOptions): ICanvasJSChartOptions {
        return {
            ...chartData,
            axisX: this.setAxisX(chartData.axisX),
            axisY: this.setAxes(chartData.axisY),
            axisY2: this.setAxes(chartData.axisY2),
            backgroundColor: this.background(),
            data: this.setColorPalette(chartData.data),
            legend: this.setLegend(chartData.legend),
            toolTip: this.setToolTip(chartData.toolTip),
        };
    }

    axisTickColor(lineColor?: string): string {
        if (lineColor === "#FFFFFF" || lineColor === "#000000") {
            return this.basicColor();
        }
        return lineColor ?? this.basicColor();
    }

    private axisLineColor(lineColor?: string): string {
        if (lineColor === undefined || lineColor === "#FFFFFF" || lineColor === "#000000") {
            return this.basicColor();
        }
        return lineColor;
    }

    private setAxisX(axisX?: ICanvasJSAxisOptions): ICanvasJSAxisOptions | undefined {
        if (!axisX) {
            return axisX;
        }
        return {
            ...axisX,
            labelFontColor: this.basicColor(),
            lineColor: this.basicColor(),
            tickColor: this.basicColor(),
            crosshair: {
                ...axisX.crosshair,
                labelBackgroundColor: this.basicColor(),
                labelFontColor: this.oppositeColor(),
            }
        };
    }

    private setAxes(
        axes?: ICanvasJSAxisOptions | ICanvasJSAxisOptions[]
    ): ICanvasJSAxisOptions | ICanvasJSAxisOptions[] | undefined {
        if (!axes) {
            return axes;
        }
        if (Array.isArray(axes)) {
            return axes.map((axis: ICanvasJSAxisOptions) => ({
                ...axis,
                labelFontColor: this.basicColor(),
                lineColor: this.axisLineColor(axis.lineColor),
                tickColor: this.axisTickColor(axis.lineColor),
                crosshair: {
                    ...axis.crosshair,
                    labelBackgroundColor: this.basicColor(),
                    labelFontColor: this.oppositeColor(),
                }
            }));
        }
        return {
            ...axes,
            labelFontColor: this.basicColor(),
            lineColor: this.axisLineColor(axes.lineColor),
            tickColor: this.axisTickColor(axes.lineColor),
            titleFontColor: this.basicColor(),
            gridColor: this.basicColor(),
        };
    }

    private setColorPalette(data?: ICanvasJSDataSeriesOptions[]): ICanvasJSDataSeriesOptions[] | undefined {
        if (!data) {
            return data;
        }
        return data.map((d, idx: number) => ({
            ...d,
            color: this.palette()[idx % this.palette().length]
        }));
    }

    private setLegend(legend?: ICanvasJSLegend): ICanvasJSLegend | undefined {
        if (!legend) {
            return legend;
        }
        return {...legend, fontColor: this.basicColor()};
    }

    private setToolTip(toolTip?: ICanvasJSToolTip): ICanvasJSToolTip | undefined {
        if (!toolTip) {
            return toolTip;
        }
        return {
            ...toolTip,
            backgroundColor: this.toolTipBackground(),
            fontColor: this.basicColor()
        };
    }
}
